// @ts-nocheck

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Avatar,
  Chip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from '@/store';
import { studies } from '@/store/studies/thunks';
import { getStudyProfile, getStudyData } from '@/store/studies/selectors';
import AtomLineChart, { LineChartSeries } from '@/components/atoms/LineChart';
import { dataService } from '@/services/data.service';

export default function StudyPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { studyId: paramStudyId } = useParams();
  const [searchParams] = useSearchParams();
  const studyId = paramStudyId || searchParams.get('study_id');
  const navigate = useNavigate();

  // Local UI state
  const [includeFields, setIncludeFields] = useState<string[]>(['revenue']);
  const [industry, setIndustry] = useState<string>('');
  const [region, setRegion] = useState<string>('');
  const [autoApply] = useState(true);

  // Redux selectors
  const studyProfile = useSelector(getStudyProfile);
  const studyData = useSelector(getStudyData);

  const [loadingLocal, setLoadingLocal] = useState({ profile: false, users: false, history: false });
  const [errorLocal, setErrorLocal] = useState<string | null>(null);
  const [apiRows, setApiRows] = useState<any[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>('revenue');
  const [loadingApi, setLoadingApi] = useState(false);

  useEffect(() => {
    if (!studyId) return;
    setLoadingLocal(s => ({ ...s, profile: true }));
    dispatch(studies.getStudyProfile(studyId)).unwrap().catch(err => setErrorLocal(String(err))).finally(() => setLoadingLocal(s => ({ ...s, profile: false })));

    setLoadingLocal(s => ({ ...s, users: false }));

    fetchHistory();
  }, [studyId]);



  const fetchHistory = useCallback(() => {
    if (!studyId) return;
    setLoadingLocal(s => ({ ...s, history: true }));
    dispatch(studies.getStudyData({ study_id: studyId, filters: includeFields }))
      .unwrap()
      .catch(err => setErrorLocal(String(err)))
      .finally(() => setLoadingLocal(s => ({ ...s, history: false })));
  }, [dispatch, studyId, includeFields, industry, region]);

  useEffect(() => {
    if (autoApply && studyId) {
      const t = setTimeout(() => fetchHistory(), 250);
      return () => clearTimeout(t);
    }
  }, [includeFields, industry, region, autoApply, studyId, fetchHistory]);

  useEffect(() => {
    if (!studyId) return;
    setLoadingApi(true);
    dataService
      .getStudyData({ study_id__eq: studyId })
      .then((resp: any) => {
        const rows = Array.isArray(resp) ? resp : resp?.data ?? [];
        setApiRows(rows || []);
      })
      .catch(err => {
        console.error('Error fetching study data (direct):', err);
      })
      .finally(() => setLoadingApi(false));
  }, [studyId]);

  const computedApiSeries = useMemo(() => {
    if (!apiRows || !Array.isArray(apiRows) || apiRows.length === 0) return null;

    const yearMap: Record<number, number> = {};
    apiRows.forEach((row: any) => {
      const year = Number(row.year ?? row.display_year ?? row.year_offset ?? null);
      if (!year || Number.isNaN(year)) return;
      const val = Number(row[selectedMetric]) || 0;
      yearMap[year] = (yearMap[year] || 0) + val;
    });

    const data = Object.keys(yearMap)
      .map(k => ({ x: Number(k), y: yearMap[Number(k)] }))
      .sort((a, b) => Number(a.x) - Number(b.x));

    const labelMap: Record<string, string> = {
      revenue: 'Revenue',
      yoy_revenue_percentage: 'YoY Revenue %',
      yoy_revenue: 'YoY Revenue',
      units_sold: 'Units Sold',
      yoy_units_sold: 'YoY Units Sold',
      yoy_units_sold_percentage: 'YoY Units Sold %',
      selling_price: 'Selling Price',
      yoy_selling_price: 'YoY Selling Price',
      avg_company_revenue: 'Avg Company Revenue',
      py_revenue: 'PY Revenue',
    };

    const color = selectedMetric.includes('yoy') || selectedMetric.includes('percentage') ? '#d32f2f' : '#ff6d00';

    return { id: `api_${selectedMetric}`, label: labelMap[selectedMetric] || selectedMetric, color, data } as LineChartSeries;
  }, [apiRows, selectedMetric]);



  const studyYears = useMemo(() => {
    const arr = studyProfile?.years_available || [];
    const nums = arr.map((y: any) => (typeof y === 'number' ? y : Number(y))).filter((n: any) => !!n);
    return nums.sort((a: number, b: number) => a - b);
  }, [studyProfile]);
  const studyYearsRange = studyYears.length ? `${studyYears[0]} — ${studyYears[studyYears.length - 1]}` : '—';


  if (loadingLocal.profile || (loadingLocal.history && !studyData?.length)) {
    return (
      <Box sx={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>STUDY DASHBOARD</Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', boxShadow: '0 8px 24px rgba(15,23,42,0.06)', backgroundColor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} textAlign={'left'} alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}> Study Name : {studyProfile?.study_name || studyProfile?.name || 'Customer Success Automation Benchmark'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 680 }}>{studyProfile?.description || 'No description provided'}</Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Chip label={`Revenue: ${typeof studyProfile?.revenue === 'number' ? studyProfile.revenue.toLocaleString() : studyProfile?.revenue || '—'}`} sx={{ borderRadius: '999px', backgroundColor: 'rgba(25,118,210,0.06)' }} />
              <Chip label={`Technology Group: ${studyProfile?.technology_group || 'Technology'}`} sx={{ borderRadius: '999px', backgroundColor: 'rgba(25,118,210,0.04)' }} />
              <Chip label={`Automation Type: ${studyProfile?.automation_type || 'Automation Type'}`} sx={{ borderRadius: '999px', backgroundColor: 'rgba(25,118,210,0.04)' }} />
            </Stack>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Study Years</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Study data available from {studyYearsRange}</Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {studyYears.length ? (
                  studyYears.map((y: number) => (
                    <Chip key={y} label={y} sx={{ borderRadius: '999px', backgroundColor: 'rgba(25,118,210,0.04)', px: 1.5 }} />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No years available</Typography>
                )}
              </Box>
            </Box>

          </Box>
          <Box sx={{ minWidth: 220, textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Study ID</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontWeight: 700, color: 'primary.dark', fontSize: 18 }}>
              {studyProfile?.id || studyId}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 2, borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 8px 24px rgba(15,23,42,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Contributors</Typography>
            {loadingLocal.users ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}><CircularProgress size={20} /><Typography variant="body2" sx={{ ml: 1 }}>Loading users...</Typography></Box>
            ) : (
              <Grid container spacing={2}>
                {(studyProfile?.user_studies || []).length > 0 ? (
                  (studyProfile.user_studies || []).slice(0, 12).map((u: any) => {
                    const role = (u.role || 'contributor').toLowerCase();
                    const roleColor = role === 'owner' ? 'primary' : role === 'analyst' ? 'success' : role === 'secondary' ? 'secondary' : 'default';
                    const avatarBg = 'primary.main' || '#1976d2';

                    return (
                      <Grid item xs={12} sm={6} key={u.id || u.user_id}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px', boxShadow: '0 8px 20px rgba(15,23,42,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: avatarBg }}>{((u.first_name || u.email || '')[0] || '?').toUpperCase()}</Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.first_name ? `${u.first_name} ${u.last_name || ''}` : u.user_name || u.email}</Typography>
                              <Typography variant="caption" color="text.secondary">{u.email || '—'}</Typography>
                            </Box>
                          </Stack>
                          <Chip label={(u.role || 'Contributor').toString()} size="small" color={roleColor as any} sx={{ borderRadius: '999px' }} />
                        </Paper>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}><Typography variant="body2">No contributors</Typography></Grid>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 2, borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 8px 24px rgba(15,23,42,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Peer Studies</Typography>

            {loadingLocal.profile ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: '12px' }} />
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: '12px' }} />
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: '12px' }} />
              </Stack>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {Array.isArray(studyProfile?.peer_studies) && studyProfile.peer_studies.length > 0 ? (
                  (studyProfile.peer_studies || []).slice(0, 6).map((p: any) => {
                    const title = p.study_name || p.name || p.title || 'Untitled Study';
                    const companies = p.number_of_companies ?? p.number_of_companies_available ?? '—';
                    const years = p.number_of_years_available ?? p.years_available ?? '—';
                    const revenue = typeof p.revenue === 'number' ? p.revenue : (p.revenue || null);
                    const tech = p.technology_group || p.technology || '—';
                    const automation = p.automation_type ? String(p.automation_type) : null;

                    return (
                      <Grid item xs={12} sm={6} key={p.id || p.study_id || title}>
                        <Paper
                          onClick={() => navigate(p.id || p.study_id ? `/study/${p.id || p.study_id}` : '#')}
                          role="button"
                          tabIndex={0}
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#fff',
                            boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
                            border: '1px solid rgba(0,0,0,0.04)',
                            cursor: p.id || p.study_id ? 'pointer' : 'default',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>{(title || '?')[0].toUpperCase()}</Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>{title}</Typography>
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Chip label={tech} size="small" sx={{ borderRadius: '999px' }} />
                                {automation && <Chip label={automation} size="small" sx={{ borderRadius: '999px' }} />}
                              </Stack>
                            </Box>
                          </Box>

                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Revenue: {revenue !== null ? Number(revenue).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}</Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Companies: {companies || '—'}</Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Years: {years || '—'}</Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}><Typography variant="body2" color="text.secondary">No peer studies available for this study.</Typography></Grid>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 2, borderRadius: '12px', backgroundColor: '#fff', boxShadow: '0 8px 24px rgba(15,23,42,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{computedApiSeries?.label ? `${computedApiSeries.label} Trend` : 'Revenue Trend'}</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="metric-select-label">Metric</InputLabel>
                  <Select
                    labelId="metric-select-label"
                    value={selectedMetric}
                    label="Metric"
                    onChange={e => setSelectedMetric(String(e.target.value))}
                  >
                    <MenuItem value="revenue">Revenue</MenuItem>
                    <MenuItem value="yoy_revenue_percentage">YoY Revenue %</MenuItem>
                    <MenuItem value="yoy_revenue">YoY Revenue</MenuItem>
                    <MenuItem value="units_sold">Units Sold</MenuItem>
                    <MenuItem value="yoy_units_sold">YoY Units Sold</MenuItem>
                    <MenuItem value="yoy_units_sold_percentage">YoY Units Sold %</MenuItem>
                    <MenuItem value="selling_price">Selling Price</MenuItem>
                    <MenuItem value="yoy_selling_price">YoY Selling Price</MenuItem>
                    <MenuItem value="avg_company_revenue">Avg Company Revenue</MenuItem>
                    <MenuItem value="py_revenue">PY Revenue</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>

            <Box sx={{ mt: 1, width: '100%', height: 'auto' }}>
              {loadingApi ? (
                <Box sx={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <AtomLineChart
                  series={[computedApiSeries].filter(Boolean) as LineChartSeries[]}
                  showHorizontalGrid={true}
                  showVerticalGrid={false}
                  xLabel="Year"
                  showTooltip={true}
                  showLegend={true}
                  formatAsQuarters={false} />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
